import { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import { Avatar } from '../components/ui';
import PremiumName from '../components/ui/PremiumName';
import { LoadingState, ErrorState } from '../components/ui';
import { useUserData } from '../hooks/useUserData';
import { useLike } from '../hooks/useLike';
import { useComments } from '../hooks/useComments';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../contexts/ToastContext';
import { getPostById, getRandomPosts, deletePost } from '../services/postService';
import { toggleSavePost } from '../services/savedService';
import { CommentsList, CommentInput } from '../components/comments';
import { PostCard, MasonryGrid } from '../components/posts';
import { formatPostDate, formatFullDate } from '../utils/dateFormatter';

const PostDetailPage = () => {
  const { postId } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [relatedLoading, setRelatedLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [shareSuccess, setShareSuccess] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const { currentUser, userData } = useAuth();
  const { showToast } = useToast();
  const { userData: postAuthor } = useUserData(post?.userId);
  const { liked, likesCount, toggleLike } = useLike(post?.id, currentUser?.uid, post?.likesCount);
  const {
    comments,
    loading: commentsLoading,
    submitting,
    addComment,
    deleteComment
  } = useComments(post?.id);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!postId) {
        setLoading(false);
        setError('ID de post no válido');
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const res = await getPostById(postId);
        if (cancelled) return;
        if (res.success && res.post) {
          setPost(res.post);
        } else {
          setError(res.error || 'Post no encontrado');
        }
      } catch (err) {
        if (!cancelled) setError(err.message || 'Error al cargar el post');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [postId]);

  useEffect(() => {
    if (!postId) return;
    let cancelled = false;
    (async () => {
      setRelatedLoading(true);
      try {
        const res = await getRandomPosts(12, postId);
        if (!cancelled && res.success) setRelatedPosts(res.posts || []);
      } catch {
        if (!cancelled) setRelatedPosts([]);
      } finally {
        if (!cancelled) setRelatedLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [postId]);

  if (loading) {
    return (
      <Layout>
        <div className="container-custom py-8">
          <LoadingState message="Cargando post..." />
        </div>
      </Layout>
    );
  }

  if (error || !post) {
    return (
      <Layout>
        <div className="container-custom py-8">
          <ErrorState
            title="Post no encontrado"
            message={error}
            actionLabel="Volver"
            onAction={() => navigate(-1)}
          />
        </div>
      </Layout>
    );
  }

  const isOwner = currentUser?.uid === post.userId;
  const savedPostIds = userData?.savedPosts || [];
  const isSaved = currentUser && Array.isArray(savedPostIds) && savedPostIds.includes(post.id);
  const hasMultipleMedia = post.media && post.media.length > 1;
  const currentMedia = post.media?.[currentImageIndex];
  const isEpisode = post.postType === 'episode';

  const handlePrevImage = () => {
    setCurrentImageIndex((prev) => (prev > 0 ? prev - 1 : post.media.length - 1));
  };

  const handleNextImage = () => {
    setCurrentImageIndex((prev) => (prev < post.media.length - 1 ? prev + 1 : 0));
  };

  const handleLike = (e) => {
    e?.stopPropagation();
    toggleLike();
  };

  const handleShare = async () => {
    const shareData = {
      title: `${postAuthor?.displayName || post.userName} en Illustra`,
      text: post.caption || 'Mira este post en Illustra',
      url: window.location.href
    };
    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(window.location.href);
        setShareSuccess(true);
        setTimeout(() => setShareSuccess(false), 3000);
      }
    } catch (err) {
      if (err.name !== 'AbortError') console.error('Error al compartir:', err);
    }
  };

  const handleAddComment = async (text) => {
    const result = await addComment(
      currentUser.uid,
      userData.displayName,
      userData.photoURL,
      text
    );
    return result.success;
  };

  const handleDeleteComment = async (commentId) => {
    await deleteComment(commentId);
  };

  const handleToggleSave = async () => {
    if (!currentUser?.uid) return;
    const result = await toggleSavePost(currentUser.uid, post.id);
    if (result.success && result.saved) showToast('Post guardado');
  };

  const handleDeletePost = async () => {
    if (!window.confirm('¿Eliminar este post?')) return;
    setDeleting(true);
    try {
      const result = await deletePost(post.id);
      if (result.success) navigate(-1);
    } catch (err) {
      console.error('Error al eliminar post:', err);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Layout>
      <div className="container-custom py-6">
        <div className="mb-4">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Volver
          </button>
        </div>

        <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-lg overflow-hidden">
          <div className="flex flex-col lg:flex-row">
            {/* Header - mobile */}
            <div className="flex lg:hidden items-center justify-between p-4 border-b border-gray-200 dark:border-slate-700">
              <Link
                to={`/profile/${post.userId}`}
                className="flex items-center gap-3 hover:opacity-80 transition-opacity"
              >
                <Avatar
                  src={postAuthor?.photoURL || post.userPhotoURL}
                  alt={postAuthor?.displayName || post.userName}
                  fallbackText={postAuthor?.displayName || post.userName}
                  size="md"
                />
                <div>
                  <PremiumName
                    name={postAuthor?.displayName || post.userName || 'Usuario'}
                    isPremium={postAuthor?.isPremium}
                    premiumColor={postAuthor?.premiumNameColor}
                    nameClassName="font-semibold text-gray-900 dark:text-white"
                  />
                  <p className="text-sm text-gray-500 dark:text-gray-400" title={formatFullDate(post.createdAt)}>
                    {formatPostDate(post.createdAt)}
                  </p>
                </div>
              </Link>
              <div className="flex items-center gap-1">
                {currentUser && (
                  <button
                    type="button"
                    onClick={handleToggleSave}
                    className={`p-2 rounded-lg transition-colors ${isSaved ? 'text-indigo-600 dark:text-indigo-400' : 'text-gray-600 dark:text-gray-400'}`}
                    title={isSaved ? 'Quitar de guardados' : 'Guardar'}
                  >
                    {isSaved ? (
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M5 2h14a1 1 0 011 1v19.143a1 1 0 01-1.581.814L12 18.499l-6.419 4.458A1 1 0 014 22.143V3a1 1 0 011-1z" />
                      </svg>
                    ) : (
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-5-7 5V5z" />
                      </svg>
                    )}
                  </button>
                )}
                {isOwner && (
                  <button
                    type="button"
                    onClick={handleDeletePost}
                    disabled={deleting}
                    className="p-2 text-red-600 dark:text-red-400 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50"
                    title="Eliminar post"
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                )}
              </div>
            </div>

            {/* Media Section */}
            <div className="flex-1 lg:w-2/3 bg-black flex items-center justify-center relative min-h-[300px] lg:min-h-[500px]">
              {isEpisode ? (
                <div className="w-full overflow-y-auto max-h-[70vh]">
                  {(post.media || []).length > 0 ? (
                    <div className="flex flex-col">
                      {(post.media || []).map((m, idx) => (
                        <img
                          key={idx}
                          src={m.url}
                          alt={post.caption || `Página ${idx + 1}`}
                          className="w-full h-auto object-contain"
                          loading={idx === 0 ? 'eager' : 'lazy'}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className="p-8 text-center">
                      <p className="text-white text-lg">{post.caption || post.text}</p>
                    </div>
                  )}
                </div>
              ) : currentMedia ? (
                <>
                  {currentMedia.type === 'video' ? (
                    <video src={currentMedia.url} controls className="w-full h-full object-contain max-h-[70vh]" />
                  ) : (
                    <img
                      src={currentMedia.url}
                      alt={`Post ${currentImageIndex + 1}`}
                      className="w-full h-full object-contain max-h-[70vh]"
                    />
                  )}
                  {hasMultipleMedia && (
                    <>
                      <button
                        onClick={handlePrevImage}
                        className="absolute left-4 top-1/2 -translate-y-1/2 p-3 bg-black/50 hover:bg-black/70 text-white rounded-full"
                      >
                        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                      </button>
                      <button
                        onClick={handleNextImage}
                        className="absolute right-4 top-1/2 -translate-y-1/2 p-3 bg-black/50 hover:bg-black/70 text-white rounded-full"
                      >
                        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-3 py-1.5 bg-black/70 rounded-full text-white text-sm">
                        {currentImageIndex + 1} / {post.media.length}
                      </div>
                    </>
                  )}
                </>
              ) : (
                <div className="p-8 text-center">
                  <p className="text-white text-lg">{post.caption || post.text}</p>
                </div>
              )}
            </div>

            {/* Info Section */}
            <div className="flex flex-col w-full lg:w-1/3 lg:min-w-[380px]">
              {/* Header - desktop */}
              <div className="hidden lg:flex items-center justify-between p-4 border-b border-gray-200 dark:border-slate-700">
                <Link
                  to={`/profile/${post.userId}`}
                  className="flex items-center gap-3 hover:opacity-80 transition-opacity"
                >
                  <Avatar
                    src={postAuthor?.photoURL || post.userPhotoURL}
                    alt={postAuthor?.displayName || post.userName}
                    fallbackText={postAuthor?.displayName || post.userName}
                    size="md"
                  />
                  <div>
                    <PremiumName
                      name={postAuthor?.displayName || post.userName || 'Usuario'}
                      isPremium={postAuthor?.isPremium}
                      premiumColor={postAuthor?.premiumNameColor}
                      nameClassName="font-semibold text-gray-900 dark:text-white"
                    />
                    <p className="text-sm text-gray-500 dark:text-gray-400" title={formatFullDate(post.createdAt)}>
                      {formatPostDate(post.createdAt)}
                    </p>
                  </div>
                </Link>
                <div className="flex items-center gap-1">
                  {currentUser && (
                    <button
                      type="button"
                      onClick={handleToggleSave}
                      className={`p-2 rounded-lg transition-colors ${isSaved ? 'text-indigo-600' : 'text-gray-600 dark:text-gray-400'}`}
                      title={isSaved ? 'Quitar de guardados' : 'Guardar'}
                    >
                      {isSaved ? (
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M5 2h14a1 1 0 011 1v19.143a1 1 0 01-1.581.814L12 18.499l-6.419 4.458A1 1 0 014 22.143V3a1 1 0 011-1z" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-5-7 5V5z" />
                        </svg>
                      )}
                    </button>
                  )}
                  {isOwner && (
                    <button
                      type="button"
                      onClick={handleDeletePost}
                      disabled={deleting}
                      className="p-2 text-red-600 dark:text-red-400 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20"
                      title="Eliminar post"
                    >
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-4 space-y-4 max-h-[50vh] lg:max-h-none">
                {post.caption && (
                  <p className="text-gray-900 dark:text-white whitespace-pre-wrap">{post.caption}</p>
                )}
                {post.tags && post.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {post.tags.map((tag, index) => (
                      <span key={index} className="text-indigo-600 dark:text-indigo-400 text-sm">
                        #{tag}
                      </span>
                    ))}
                  </div>
                )}
                {post.isService && post.servicePrice && (
                  <div className="p-4 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg border border-indigo-200 dark:border-indigo-800">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Servicio disponible</span>
                      <span className="text-lg font-bold text-indigo-600 dark:text-indigo-400">${post.servicePrice}</span>
                    </div>
                    <button
                      type="button"
                      className="w-full px-4 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-medium rounded-lg text-sm"
                    >
                      Contratar servicio
                    </button>
                  </div>
                )}

                <div className="pt-4 border-t border-gray-200 dark:border-slate-700">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">
                    Comentarios ({comments.length})
                  </h3>
                  <CommentsList
                    comments={comments}
                    currentUserId={currentUser?.uid}
                    onDeleteComment={handleDeleteComment}
                    loading={commentsLoading}
                  />
                </div>
              </div>

              <div className="p-4 border-t border-gray-200 dark:border-slate-700 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <button
                      type="button"
                      onClick={handleLike}
                      className="flex items-center gap-2 hover:scale-110 transition-transform disabled:opacity-50"
                      disabled={!currentUser}
                    >
                      <svg
                        className={`w-6 h-6 ${liked ? 'fill-red-500 text-red-500' : 'fill-none text-gray-600 dark:text-gray-400'}`}
                        viewBox="0 0 20 20"
                        stroke="currentColor"
                        strokeWidth={liked ? 0 : 2}
                      >
                        <path fillRule="evenodd" d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" clipRule="evenodd" />
                      </svg>
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{likesCount}</span>
                    </button>
                    <div className="flex items-center gap-2">
                      <svg className="w-6 h-6 text-gray-600 dark:text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                      </svg>
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{comments.length}</span>
                    </div>
                  </div>
                  <button type="button" onClick={handleShare} className="relative" title="Compartir">
                    <svg className="w-6 h-6 text-gray-600 dark:text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                    </svg>
                    {shareSuccess && (
                      <span className="absolute -top-10 right-0 px-3 py-1.5 bg-green-500 text-white text-xs rounded-lg shadow-lg">
                        ¡Copiado!
                      </span>
                    )}
                  </button>
                </div>
                {currentUser && (
                  <CommentInput currentUser={userData} onSubmit={handleAddComment} submitting={submitting} />
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Más para explorar */}
        <div className="mt-12">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6">Más para explorar</h2>
          {relatedLoading ? (
            <LoadingState message="Cargando posts..." />
          ) : relatedPosts.length > 0 ? (
            <MasonryGrid>
              {relatedPosts.map((p) => (
                <PostCard key={p.id} post={p} currentUserId={userData?.id} savedPostIds={userData?.savedPosts || []} />
              ))}
            </MasonryGrid>
          ) : (
            <p className="text-gray-500 dark:text-gray-400 py-8 text-center">No hay más posts para mostrar.</p>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default PostDetailPage;
